import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ImageModal from './ImageModal';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background: linear-gradient(45deg, #ff6b6b 0%, #4ecdc4 25%, #45b7d1 50%, #96ceb4 75%, #ffeaa7 100%);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Header = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  position: relative;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UserName = styled.span`
  color: #000000;
  font-weight: 900;
  background: #ffeb3b;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    background: #4ecdc4;
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0px #000000;
  }
`;

const LogoutButton = styled.button`
  background: #ff6b6b;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 12px 20px;
  color: #000000;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    background: #96ceb4;
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0px #000000;
  }
`;

const NavLink = styled(Link)`
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 900;
  color: #000000;
  text-align: center;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 4px 4px 0px #ffffff;
  font-family: 'Arial Black', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #000000;
  text-align: center;
  margin-bottom: 40px;
  font-weight: 700;
  background: #ffeaa7;
  padding: 10px 20px;
  border: 3px solid #000000;
  transform: rotate(-1deg);
`;

const Card = styled.div`
  background: #ffffff;
  border: 6px solid #000000;
  border-radius: 0;
  padding: 40px;
  width: 100%;
  max-width: 600px;
  box-shadow: 12px 12px 0px #000000;
  position: relative;
  overflow: hidden;
  transform: rotate(1deg);
  
  &:hover {
    transform: rotate(-1deg) translate(-4px, -4px);
    box-shadow: 16px 16px 0px #000000;
  }
`;

const TextInput = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 20px;
  border: 4px solid #000000;
  border-radius: 0;
  background: #f8f8f8;
  color: #000000;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  font-weight: 600;
  box-shadow: 4px 4px 0px #4ecdc4;
  
  &::placeholder {
    color: #666666;
    font-weight: 600;
  }
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    background: #ffffff;
    box-shadow: 6px 6px 0px #ff6b6b;
    transform: translate(-2px, -2px);
  }
`;

const PromptLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 16px;
  background: #45b7d1;
  border: 3px solid #000000;
  border-radius: 0;
  color: #000000;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  font-weight: 700;
  text-transform: uppercase;
  box-shadow: 3px 3px 0px #000000;
  
  &:hover {
    background: #96ceb4;
    transform: translate(-2px, -2px);
    box-shadow: 5px 5px 0px #000000;
  }
`;

const SizeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 10px;
  margin: 30px 0;
`;

const SizeOption = styled.button`
  padding: 15px 10px;
  border: 4px solid #000000;
  border-radius: 0;
  background: ${props => props.selected ? '#ffeb3b' : '#ffffff'};
  color: #000000;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: ${props => props.selected ? '4px 4px 0px #ff6b6b' : '4px 4px 0px #4ecdc4'};
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    background: #96ceb4;
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0px #000000;
  }
  
  &:active {
    transform: translate(2px, 2px);
    box-shadow: 2px 2px 0px #000000;
  }
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 20px;
  background: ${props => props.disabled ? '#cccccc' : '#45b7d1'};
  border: 4px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 900;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: ${props => props.disabled ? '4px 4px 0px #999999' : '6px 6px 0px #000000'};
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translate(-2px, -2px)'};
    background: ${props => props.disabled ? '#cccccc' : '#96ceb4'};
    box-shadow: ${props => props.disabled ? '4px 4px 0px #999999' : '8px 8px 0px #000000'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translate(2px, 2px)'};
    box-shadow: ${props => props.disabled ? '4px 4px 0px #999999' : '2px 2px 0px #000000'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultSection = styled.div`
  width: 100%;
  max-width: 800px;
  margin-top: 40px;
`;

const ResultTitle = styled.h2`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.8rem;
  font-weight: 600;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const ImageCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 300px;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const ProgressText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
`;

const sizeOptions = [
  { value: '1:1', label: '1:1' },
  { value: '2:3', label: '2:3' },
  { value: '9:16', label: '9:16' },
  { value: '4:3', label: '4:3' },
  { value: '3:2', label: '3:2' },
  { value: '235:100', label: '公众号封面图' },
  { value: '16:9', label: '16:9' }
];

function TextToImage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedSize, setSelectedSize] = useState('1:1');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const handleUserClick = () => {
    navigate(`/user-center/${user?.id}`);
  };

  const pollTaskStatus = async (taskId) => {
    try {
      const response = await axios.get(`/api/fetch?taskId=${taskId}`);
      
      if (response.data.success) {
        const { status, progress: taskProgress, images: taskImages } = response.data;
        
        setProgress(taskProgress || 0);
        
        if (status === 'completed' && taskImages && taskImages.length > 0) {
          setImages(taskImages);
          setLoading(false);
          setCurrentTaskId(null);
          return;
        } else if (status === 'failed') {
          alert('图片生成失败，请重试');
          setLoading(false);
          setShowResults(false);
          setCurrentTaskId(null);
          setProgress(0);
          return;
        }
        
        // 如果任务还在进行中，继续轮询
        if (status === 'pending' || status === 'processing') {
          setTimeout(() => pollTaskStatus(taskId), 10000); // 每10秒查询一次
        }
      } else {
        console.error('查询任务状态失败:', response.data.message);
        setTimeout(() => pollTaskStatus(taskId), 10000); // 出错时延长间隔
      }
    } catch (error) {
      console.error('查询任务状态出错:', error);
      setTimeout(() => pollTaskStatus(taskId), 10000); // 出错时延长间隔
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('请输入图片描述');
      return;
    }

    if (loading) {
      return; // 防止重复提交
    }

    setLoading(true);
    setShowResults(true);
    setProgress(0);
    setImages([]);
    
    try {
      const response = await axios.post('/api/draw', {
        prompt: prompt.trim(),
        aspectRatio: selectedSize,
        userId: user?.id
      });
      
      if (response.data.success && response.data.taskId) {
        setCurrentTaskId(response.data.taskId);
        // 开始轮询任务状态
        setTimeout(() => pollTaskStatus(response.data.taskId), 2000);
      } else {
        alert(response.data.message || '任务提交失败，请重试');
        setShowResults(false);
        setLoading(false);
      }
    } catch (error) {
      console.error('提交任务失败:', error);
      alert('提交任务失败，请检查网络连接后重试');
      setShowResults(false);
      setLoading(false);
      setProgress(0);
      setCurrentTaskId(null);
    }
  };

  return (
    <Container>
      <Header>
        <UserInfo>
          <UserName onClick={handleUserClick}>👋 {user?.username}</UserName>
          <span style={{
            color: '#000000',
            background: '#ff6b6b',
            fontSize: '12px',
            marginLeft: '8px',
            fontWeight: '900',
            padding: '4px 8px',
            border: '3px solid #000000',
            borderRadius: '0',
            boxShadow: '3px 3px 0px #000000',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: 'Arial Black, Arial, sans-serif',
            transform: 'rotate(-1deg)',
            display: 'inline-block',
            transition: 'all 0.2s ease'
          }}>
            👈点击进入个人空间查看全部创作记录
          </span>
        </UserInfo>
        <UserInfo>
 
          <LogoutButton onClick={handleLogout}>
            🚪 退出登录
          </LogoutButton>
        </UserInfo>
      </Header>
      
      <Title>👑 尊贵的知识星球会员专享</Title>
      <Subtitle>梭哈AI为您提供的专属Midjourney画图服务 · 尊享无限创作</Subtitle>
      
      <Card>
        <TextInput
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="尊贵的会员，请描述您想要创作的艺术作品，例如：赛博朋克风格的未来城市，霓虹灯闪烁，高清细节..."
        />
        
        <PromptLink href="http://musesai.io/" target="_blank">
          💡 获取提示词灵感
        </PromptLink>
        
        <SizeOptions>
          {sizeOptions.map(option => (
            <SizeOption
              key={option.value}
              selected={selectedSize === option.value}
              onClick={() => setSelectedSize(option.value)}
            >
              {option.label}
            </SizeOption>
          ))}
        </SizeOptions>
        
        <GenerateButton
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? '🎨 正在为您精心创作...' : '✨ 开启专属创作之旅'}
        </GenerateButton>
        
        {loading && (
          <div style={{
            marginTop: '20px',
            padding: '20px',
            background: '#ffeb3b',
            border: '4px solid #000000',
            borderRadius: '0',
            color: '#000000',
            fontSize: '16px',
            lineHeight: '1.6',
            textAlign: 'center',
            fontWeight: '900',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: 'Arial Black, Arial, sans-serif',
            boxShadow: '6px 6px 0px #000000',
            transform: 'rotate(-1deg)',
            position: 'relative'
          }}>
            💡 任务已开始执行，无需等待！您可以离开此页面，到
            <span 
              onClick={() => navigate(`/user-center/${user?.id}`)}
              style={{
                color: '#ffffff',
                background: '#ff6b6b',
                cursor: 'pointer',
                textDecoration: 'none',
                margin: '0 6px',
                fontWeight: '900',
                padding: '4px 12px',
                border: '3px solid #000000',
                borderRadius: '0',
                boxShadow: '3px 3px 0px #000000',
                display: 'inline-block',
                transform: 'rotate(1deg)',
                transition: 'all 0.2s ease',
                fontFamily: 'Arial Black, Arial, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'rotate(1deg) scale(1.05)';
                e.target.style.boxShadow = '4px 4px 0px #000000';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'rotate(1deg) scale(1)';
                e.target.style.boxShadow = '3px 3px 0px #000000';
              }}
            >
              个人中心
            </span>
            查看进行中的任务
          </div>
        )}
      </Card>
      
      {showResults && (
        <ResultSection>
          <ResultTitle>
            {loading ? '🎨 AI正在为您创作...' : '✨ 您的专属Midjourney作品'}
          </ResultTitle>
          
          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill progress={progress} />
                </ProgressBar>
                <ProgressText>{progress}% 完成</ProgressText>
              </ProgressContainer>
            </LoadingContainer>
          ) : (
            <ImageGrid>
              {images.map((imageUrl, index) => (
                <ImageCard key={index} onClick={() => handleImageClick(imageUrl)}>
                  <Image src={imageUrl} alt={`生成的图片 ${index + 1}`} />
                </ImageCard>
              ))}
            </ImageGrid>
          )}
        </ResultSection>
      )}
      
      <ImageModal 
        isOpen={isModalOpen}
        imageUrl={selectedImage}
        onClose={handleCloseModal}
      />
    </Container>
  );
}

export default TextToImage;